import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, Link, Stack, Typography } from '@mui/material';
import {
  MailOutlineRounded as MailIcon,
  BugReportOutlined as BugIcon,
  DeleteOutlineRounded as DeleteIcon,
  LockResetRounded as ResetIcon,
} from '@mui/icons-material';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import PublicLayout from '../../components/layout/PublicLayout';
import { SUPPORT_EMAIL } from './legalContent';

// The support page.
//
// The App Store requires a support URL that a reviewer can open without an
// account, and the reviewer will check two things in particular: that a
// human can be reached, and that account deletion is explained. Both are
// above the fold.

const Placeholder: React.FC<{ children: string }> = ({ children }) => (
  <Box component="mark" sx={{ bgcolor: 'warning.light', color: 'warning.dark', fontWeight: 700, px: 0.75, borderRadius: 1 }}>
    {children.replace(/[[\]]/g, '')}
  </Box>
);

const Item: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon, title, children,
}) => (
  <Card sx={{ p: 2.5 }}>
    <Stack direction="row" gap={2}>
      <Box sx={{ color: 'primary.main', pt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography variant="h5" sx={{ mb: 0.5 }}>{title}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>{children}</Typography>
      </Box>
    </Stack>
  </Card>
);

const Support: React.FC = () => {
  useDocumentTitle('Support');
  return (
  <PublicLayout>
    <Typography variant="h1" sx={{ mb: 1 }}>Support</Typography>
    <Typography variant="body1" color="text.secondary" sx={{ fontSize: 17, mb: 4 }}>
      A person reads this inbox. Write in plain language and tell us what you were doing when it went wrong.
    </Typography>

    <Stack gap={2}>
      <Item icon={<MailIcon />} title="Email us">
        <Placeholder>{SUPPORT_EMAIL}</Placeholder> — we answer within one business day.
        If something is stopping work right now, put URGENT in the subject.
      </Item>

      <Item icon={<BugIcon />} title="Report a problem from the app">
        Every screen in the mobile app has a bug report form at the bottom of Home. It sends us the
        screen you were on and what your account can see, which usually saves a round of questions.
      </Item>

      <Item icon={<ResetIcon />} title="Forgotten password">
        Use <Link component={RouterLink} to="/forgot-password" underline="hover">Forgot password</Link> on
        the sign-in screen. If the email does not arrive, check spam and then write to us — we can
        see whether it was sent.
      </Item>

      <Item icon={<DeleteIcon />} title="Delete your account">
        In the mobile app: <strong>Settings → Delete my account</strong>. It is permanent. Your login,
        name, email, phone, photo and devices are deleted, along with your membership of every company.
        Compliance documents the company holds about you, the audit trail, and messages you sent stay
        with the company — it is legally required to keep them. The{' '}
        <Link component={RouterLink} to="/privacy" underline="hover">privacy policy</Link> explains this
        in full. If you are the only owner of a company, make somebody else an owner first.
      </Item>
    </Stack>

  </PublicLayout>
  );
};

export default Support;
